import { type SnapshotFrom } from 'xstate';
import { type machine } from '#src/state-machine/definition';
import { getFiltersLimits } from '#src/state-machine/selectors';
import { type Compare, type ParentOption } from '#src/types';
import { translate } from '#src/utils/i18n/translate';

export interface ExportData {
  dataset: string;
  level: string;
  levelDetailed: string;
  year: string;
  category: string;
  categoryDetailed: string;
}

/**
 * Builds a label suffix for a parent option based on selection and limit
 */
export function buildLabelSuffix(
  parent: ParentOption | undefined,
  selection: Compare,
  comparisonLimit: number | undefined
): {
  suffix: string | null;
  title: string | null;
} {
  if (
    !parent ||
    comparisonLimit == null ||
    !parent.options ||
    (selection.length === 0 && comparisonLimit === 1)
  ) {
    return { suffix: null, title: null };
  }

  if (selection.length === 0 && comparisonLimit > 1) {
    const label = `(${translate('filters.compare.top-items-label')})`.replace(
      '{count}',
      comparisonLimit.toString()
    );
    return { suffix: label, title: label };
  }

  if (selection.length === 0 && comparisonLimit === 0) {
    const label = `(${translate('filters.compare.all-items-label')})`;
    return {
      suffix: label,
      title: label,
    };
  }

  if (selection.length < 3) {
    const names: string[] = [];
    const optionsById = parent.options.reduce(
      (acc, o) => {
        acc[o.id] = o.name;
        return acc;
      },
      {} as Record<string, string>
    );
    selection.forEach((id) => {
      let name = optionsById[id[0] as string];
      names.push(name);
    });

    const abbreviatedNames = names
      .map((name) => {
        if (names.length >= 1 && name.length > 16) {
          return name.substring(0, 23) + '.';
        }
        return name;
      })
      .join(', ');

    return { suffix: `(${abbreviatedNames})`, title: `(${names.join(', ')})` };
  }
  return {
    suffix: `(${selection.length.toString()})`,
    title: `(${selection.length.toString()})`,
  };
}

/**
 * Extracts export data from the state object
 */
export function extractExportData(
  state: SnapshotFrom<typeof machine>
): ExportData {
  const { context } = state;
  const limits = getFiltersLimits(state);

  const dataset =
    context.data.datasetOptions.find((d) => d.id === context.dataset)?.name ||
    '';

  // Build level display values
  const levelParent = context.data.levelOptions?.[context.level];
  let level = levelParent?.name || '';
  let levelDetailed = level;

  if (levelParent) {
    const levelLimit =
      limits.level[context.level]?.comparisonLimit ??
      limits.level.default?.comparisonLimit;
    const levelSuffix = buildLabelSuffix(
      levelParent,
      context.levelCompare,
      levelLimit
    );
    if (levelSuffix.suffix) {
      levelDetailed = `${levelParent.name} ${levelSuffix.suffix}`;
    }
  }

  const year =
    context.yearsMode === 'single'
      ? context.selectedYears[0].toString()
      : context.yearsMode === 'multiple'
        ? context.selectedYears.join(', ')
        : `${context.selectedYears[0]}-${context.selectedYears[context.selectedYears.length - 1]}`;

  // Build category display values
  const categoryParent = context.data.categoryOptions?.[context.category];
  let category = categoryParent?.name || '';
  let categoryDetailed = category;

  if (categoryParent) {
    const categoryLimit =
      limits.category[context.category]?.comparisonLimit ??
      limits.category.default?.comparisonLimit;
    const categorySuffix = buildLabelSuffix(
      categoryParent,
      context.categoryCompare,
      categoryLimit
    );
    if (categorySuffix.suffix) {
      categoryDetailed = `${categoryParent.name} ${categorySuffix.suffix}`;
    }
  }

  return {
    dataset,
    level,
    levelDetailed,
    year,
    category,
    categoryDetailed,
  };
}

/**
 * Generates a filename for CSV export from the export data
 */
export function generateCsvFilename(data: ExportData): string {
  const parts = ['gfx', data.dataset, data.level, data.year, data.category]
    .filter(Boolean)
    .map((part) => part.toLowerCase().replace(/\s+/g, '-'));

  return `${parts.join('_')}.csv`;
}
